import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Search, Play, Trophy, Star, CheckCircle, XCircle, ChevronRight, Database, HelpCircle } from 'lucide-react';
import { useAcademyStore } from '../../store/academyStore';

interface Case {
    id: number;
    title: string;
    story: string;
    table: { columns: string[]; rows: string[][] };
    question: string;
    hint: string;
    expectedKeywords: string[];
    solution: string;
    points: number;
}

const CASES: Case[] = [
    {
        id: 1,
        title: 'The Missing Bananas',
        story: 'Detective, we have a crisis! Someone stole all the bananas from the jungle storage. We need to find orders with more than 50 bananas.',
        table: {
            columns: ['order_id', 'customer', 'item', 'quantity'],
            rows: [
                ['1', 'Gorilla Joe', 'bananas', '100'],
                ['2', 'Chimp Charlie', 'apples', '30'],
                ['3', 'Orangutan Oscar', 'bananas', '75'],
                ['4', 'Gorilla Joe', 'grapes', '25'],
                ['5', 'Lemur Larry', 'bananas', '45'],
            ]
        },
        question: 'Find all orders where quantity is greater than 50',
        hint: 'Use SELECT * FROM orders WHERE quantity > 50',
        expectedKeywords: ['select', 'from', 'where', '>', '50'],
        solution: "SELECT * FROM orders WHERE quantity > 50",
        points: 50,
    },
    {
        id: 2,
        title: 'The VIP List',
        story: 'The Gorilla Gala is coming up! We need to find all customers whose names start with "Gorilla" to send them VIP invites.',
        table: {
            columns: ['customer_id', 'name', 'email', 'membership'],
            rows: [
                ['1', 'Gorilla Joe', 'joe@jungle.com', 'gold'],
                ['2', 'Chimp Charlie', 'charlie@trees.com', 'silver'],
                ['3', 'Gorilla Grace', 'grace@jungle.com', 'gold'],
                ['4', 'Gorilla Gary', 'gary@jungle.com', 'bronze'],
                ['5', 'Lemur Larry', 'larry@leaves.com', 'silver'],
            ]
        },
        question: 'Find all customers whose name starts with "Gorilla"',
        hint: "Use LIKE 'Gorilla%' pattern",
        expectedKeywords: ['select', 'from', 'where', 'like', 'gorilla%'],
        solution: "SELECT * FROM customers WHERE name LIKE 'Gorilla%'",
        points: 75,
    },
    {
        id: 3,
        title: 'Top Spenders',
        story: 'Finance needs a report! Find the top 3 orders by total amount, ordered from highest to lowest.',
        table: {
            columns: ['order_id', 'customer', 'total_amount'],
            rows: [
                ['1', 'Gorilla Joe', '250'],
                ['2', 'Chimp Charlie', '175'],
                ['3', 'Orangutan Oscar', '500'],
                ['4', 'Gorilla Grace', '325'],
                ['5', 'Lemur Larry', '150'],
            ]
        },
        question: 'Find top 3 orders sorted by total_amount descending',
        hint: 'Use ORDER BY ... DESC LIMIT 3',
        expectedKeywords: ['select', 'from', 'order by', 'desc', 'limit', '3'],
        solution: "SELECT * FROM orders ORDER BY total_amount DESC LIMIT 3",
        points: 100,
    },
    {
        id: 4,
        title: 'The Count',
        story: 'How many unique customers do we have? The marketing team needs this number ASAP!',
        table: {
            columns: ['order_id', 'customer_id', 'item'],
            rows: [
                ['1', '101', 'bananas'],
                ['2', '102', 'apples'],
                ['3', '101', 'grapes'],
                ['4', '103', 'bananas'],
                ['5', '101', 'oranges'],
            ]
        },
        question: 'Count the number of distinct customer_ids',
        hint: 'Use COUNT(DISTINCT column)',
        expectedKeywords: ['select', 'count', 'distinct', 'customer_id', 'from'],
        solution: "SELECT COUNT(DISTINCT customer_id) FROM orders",
        points: 100,
    },
    {
        id: 5,
        title: 'Group Analysis',
        story: 'The boss wants to know: what is the total quantity ordered for EACH item? Group the results!',
        table: {
            columns: ['order_id', 'item', 'quantity'],
            rows: [
                ['1', 'bananas', '100'],
                ['2', 'apples', '30'],
                ['3', 'bananas', '75'],
                ['4', 'grapes', '25'],
                ['5', 'apples', '45'],
            ]
        },
        question: 'Find the total quantity for each item type',
        hint: 'Use GROUP BY with SUM()',
        expectedKeywords: ['select', 'sum', 'quantity', 'group by', 'item'],
        solution: "SELECT item, SUM(quantity) FROM orders GROUP BY item",
        points: 125,
    },
];

export const QueryQuest = () => {
    const { completeModule } = useAcademyStore();
    const [currentCase, setCurrentCase] = useState(0);
    const [query, setQuery] = useState('');
    const [result, setResult] = useState<'correct' | 'incorrect' | null>(null);
    const [showHint, setShowHint] = useState(false);
    const [solvedCases, setSolvedCases] = useState<number[]>([]);
    const [totalScore, setTotalScore] = useState(0);
    const [showSolution, setShowSolution] = useState(false);

    const caseData = CASES[currentCase];

    const checkQuery = () => {
        const normalizedQuery = query.toLowerCase().replace(/\s+/g, ' ').trim();
        const allKeywordsFound = caseData.expectedKeywords.every(keyword =>
            normalizedQuery.includes(keyword.toLowerCase())
        );

        if (allKeywordsFound) {
            setResult('correct');
            if (!solvedCases.includes(caseData.id)) {
                setSolvedCases([...solvedCases, caseData.id]);
                setTotalScore(prev => prev + caseData.points);

                if (solvedCases.length + 1 >= 3) {
                    completeModule('data-detective-sql', 100);
                }
            }
        } else {
            setResult('incorrect');
        }
    };

    const nextCase = () => {
        if (currentCase < CASES.length - 1) {
            setCurrentCase(currentCase + 1);
            setQuery('');
            setResult(null);
            setShowHint(false);
            setShowSolution(false);
        }
    };

    const resetCase = () => {
        setQuery('');
        setResult(null);
        setShowHint(false);
        setShowSolution(false);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-amber-900/20 to-slate-900 font-sans text-white">
            {/* Header */}
            <div className="bg-slate-800/80 backdrop-blur-xl border-b border-slate-700 px-8 py-4 flex justify-between items-center sticky top-0 z-50">
                <Link to="/" className="flex items-center gap-2 text-slate-400 font-bold hover:text-amber-400 transition-colors">
                    <Home size={20} /> Dashboard
                </Link>
                <div className="bg-amber-900/50 text-amber-400 px-4 py-2 rounded-xl text-base font-bold border border-amber-800 flex items-center gap-2">
                    <Search size={20} /> Query Quest
                </div>
            </div>

            <div className="max-w-6xl mx-auto p-8">
                {/* Title */}
                <div className="text-center mb-8">
                    <h1 className="text-5xl font-black text-white mb-3 flex items-center justify-center gap-4">
                        <Search className="text-amber-400" size={48} /> Query Quest
                    </h1>
                    <p className="text-xl text-slate-400 max-w-2xl mx-auto">
                        Solve mysteries with SQL! Write queries to crack each case.
                    </p>
                </div>

                {/* Progress */}
                <div className="flex justify-center gap-4 mb-8 flex-wrap">
                    <div className="bg-slate-800 px-8 py-4 rounded-2xl border border-slate-700 text-center">
                        <div className="text-4xl font-black text-amber-400">{totalScore}</div>
                        <div className="text-sm text-slate-500 font-bold uppercase">Score</div>
                    </div>
                    <div className="bg-slate-800 px-8 py-4 rounded-2xl border border-slate-700 text-center">
                        <div className="text-4xl font-black text-green-400">{solvedCases.length}/{CASES.length}</div>
                        <div className="text-sm text-slate-500 font-bold uppercase">Solved</div>
                    </div>
                    <div className="bg-slate-800 px-8 py-4 rounded-2xl border border-slate-700 text-center">
                        <div className="text-4xl font-black text-purple-400">{caseData.points}</div>
                        <div className="text-sm text-slate-500 font-bold uppercase">Case Points</div>
                    </div>
                </div>

                {/* Case Selection */}
                <div className="flex justify-center gap-3 mb-8">
                    {CASES.map((c, i) => (
                        <button
                            key={c.id}
                            onClick={() => { setCurrentCase(i); resetCase(); }}
                            className={`w-12 h-12 rounded-xl font-bold text-lg flex items-center justify-center transition-all border-2 ${currentCase === i
                                ? 'bg-amber-500 text-white border-amber-400 scale-110'
                                : solvedCases.includes(c.id)
                                    ? 'bg-green-900/50 text-green-400 border-green-700'
                                    : 'bg-slate-800 text-slate-500 border-slate-700 hover:border-slate-500'
                                }`}
                        >
                            {solvedCases.includes(c.id) ? <CheckCircle size={20} /> : i + 1}
                        </button>
                    ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Case Details */}
                    <div className="space-y-6">
                        {/* Story */}
                        <motion.div
                            className="bg-slate-800 rounded-2xl p-6 border border-slate-700"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            key={currentCase}
                        >
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center">
                                    <Database size={24} />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-white">Case #{caseData.id}: {caseData.title}</h2>
                                    <div className="text-sm text-amber-400 font-bold">{caseData.points} points</div>
                                </div>
                            </div>
                            <p className="text-lg text-slate-300 leading-relaxed">{caseData.story}</p>
                        </motion.div>

                        {/* The Question */}
                        <div className="bg-amber-900/30 rounded-2xl p-6 border-2 border-amber-700">
                            <h3 className="font-bold text-amber-400 mb-2 flex items-center gap-2">
                                <Star size={18} /> Your Mission
                            </h3>
                            <p className="text-xl text-white font-medium">{caseData.question}</p>
                        </div>

                        {/* Data Table */}
                        <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700 overflow-x-auto">
                            <h3 className="font-bold text-white mb-4 flex items-center gap-2">
                                <Database size={18} /> Evidence (Table Data)
                            </h3>
                            <table className="w-full text-left">
                                <thead>
                                    <tr className="border-b border-slate-700">
                                        {caseData.table.columns.map(col => (
                                            <th key={col} className="py-3 px-4 text-amber-400 font-bold text-base">{col}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {caseData.table.rows.map((row, i) => (
                                        <tr key={i} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                                            {row.map((cell, j) => (
                                                <td key={j} className="py-3 px-4 text-slate-300 font-mono">{cell}</td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Query Input */}
                    <div className="space-y-6">
                        <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
                            <h3 className="font-bold text-white mb-4">💻 Write Your Query</h3>
                            <textarea
                                value={query}
                                onChange={e => { setQuery(e.target.value); setResult(null); }}
                                placeholder="SELECT * FROM ..."
                                className="w-full h-40 bg-slate-900 border-2 border-slate-700 rounded-xl p-4 text-green-400 font-mono text-lg focus:border-amber-500 outline-none resize-none"
                            />

                            <div className="flex gap-4 mt-4">
                                <button
                                    onClick={checkQuery}
                                    className="btn btn-lg flex-1 bg-gradient-to-r from-amber-500 to-orange-600 text-white"
                                >
                                    <Play size={20} fill="currentColor" /> Execute Query
                                </button>
                                <button
                                    onClick={() => setShowHint(!showHint)}
                                    className="btn btn-lg bg-slate-700 text-slate-300 hover:bg-slate-600"
                                >
                                    <HelpCircle size={20} /> Hint
                                </button>
                            </div>
                        </div>

                        {/* Hint */}
                        <AnimatePresence>
                            {showHint && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="bg-blue-900/30 rounded-2xl p-6 border border-blue-700"
                                >
                                    <h4 className="font-bold text-blue-400 mb-2">💡 Hint</h4>
                                    <p className="text-blue-200 font-mono">{caseData.hint}</p>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Result */}
                        <AnimatePresence>
                            {result && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                    className={`rounded-2xl p-6 border-2 ${result === 'correct'
                                        ? 'bg-green-900/30 border-green-500'
                                        : 'bg-red-900/30 border-red-500'
                                        }`}
                                >
                                    <div className="flex items-center gap-4">
                                        {result === 'correct' ? (
                                            <>
                                                <CheckCircle className="text-green-400" size={48} />
                                                <div>
                                                    <h4 className="text-2xl font-bold text-green-400">Case Solved!</h4>
                                                    <p className="text-green-300">+{caseData.points} points earned!</p>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <XCircle className="text-red-400" size={48} />
                                                <div>
                                                    <h4 className="text-2xl font-bold text-red-400">Not Quite Right</h4>
                                                    <p className="text-red-300">Check your syntax and try again!</p>
                                                    <button
                                                        onClick={() => setShowSolution(!showSolution)}
                                                        className="text-sm text-red-400 underline mt-2"
                                                    >
                                                        {showSolution ? 'Hide Solution' : 'Show Solution'}
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    {showSolution && result === 'incorrect' && (
                                        <div className="mt-4 p-4 bg-slate-900 rounded-xl">
                                            <div className="text-sm text-slate-400 mb-1">Solution:</div>
                                            <code className="text-green-400 font-mono">{caseData.solution}</code>
                                        </div>
                                    )}

                                    {result === 'correct' && currentCase < CASES.length - 1 && (
                                        <button
                                            onClick={nextCase}
                                            className="mt-4 btn bg-green-600 text-white hover:bg-green-500"
                                        >
                                            Next Case <ChevronRight size={18} />
                                        </button>
                                    )}
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* SQL Cheat Sheet */}
                        <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700">
                            <h3 className="font-bold text-white mb-4">📋 SQL Quick Reference</h3>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div className="space-y-2">
                                    <div className="font-mono text-green-400">SELECT * FROM table</div>
                                    <div className="font-mono text-green-400">WHERE column = value</div>
                                    <div className="font-mono text-green-400">WHERE column {'>'} 10</div>
                                </div>
                                <div className="space-y-2">
                                    <div className="font-mono text-green-400">ORDER BY column DESC</div>
                                    <div className="font-mono text-green-400">LIMIT 5</div>
                                    <div className="font-mono text-green-400">GROUP BY column</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
